import { getWhtVarianceSummary } from '@/api/dashboardApi'
import { whtVarianceSummaryUIMetaData } from '@/constants/dashboardConstant'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, Tooltip, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

const WHTVarianceSummary = () => {
  // ==================== Hooks ====================
  const optionsLoaded = useSelector(
    state => state.dashboardFilter.optionsLoaded
  )
  const appliedFilters = useSelector(
    state => state.dashboardFilter.appliedFilters
  )

  // ==================== State ====================
  const [data, setData] = useState([])
  const [dataLoaded, setDataLoaded] = useState(false)

  // ==================== Derived Data ====================
  const rowColors = [
    'var(--bg-yellow-light)',
    'var(--bg-light-orange)',
    'var(--bg-light-green)',
  ]

  // ==================== Effects ====================
  useEffect(() => {
    if (!optionsLoaded) return
    const fetchData = async () => {
      setDataLoaded(false)
      try {
        const varianceSummaryResponse =
          await getWhtVarianceSummary(appliedFilters)
        processData(varianceSummaryResponse)
      } finally {
        setDataLoaded(true)
      }
    }
    fetchData()
  }, [optionsLoaded, appliedFilters])

  // ==================== Component Helpers ====================
  const processData = varianceSummaryResponse => {
    const varianceSummaryData =
      varianceSummaryResponse?.data?.data?.varianceCards
    if (varianceSummaryResponse?.success && varianceSummaryData) {
      setData(varianceSummaryData)
    } else {
      setData([])
    }
  }

  // ==================== Return ====================
  return (
    <Box
      sx={{
        border: '1px solid var(--border-subtel)',
        backgroundColor: '#fff',
        flex: '1 1 0',
        minWidth: '300px',
        height: '323px',
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        component="section"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 2.5,
        }}
      >
        <Typography
          sx={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 500,
            color: 'var(--forbrand-primary)',
            lineHeight: 1.4,
          }}
        >
          {whtVarianceSummaryUIMetaData.heading}
        </Typography>

        <Tooltip title={whtVarianceSummaryUIMetaData.subHeading}>
          <InfoOutlinedIcon sx={{ width: '17.5px', height: '17.47px' }} />
        </Tooltip>
      </Box>

      {!dataLoaded ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              color: 'var(--forbrand-primary)',
              fontSize: 'var(--font-size-lg)',
            }}
          >
            Loading...
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Header row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              pb: 1,
              gap: 2,
            }}
          >
            <Box sx={{ flex: 1 }} />
            <Typography
              sx={{
                minWidth: '70px',
                textAlign: 'right',
                fontSize: 'var(--font-size-md)',
                fontWeight: 400,
                color: 'var(--forbrand-primary)',
                whiteSpace: 'nowrap',
              }}
            >
              Records
            </Typography>
            <Typography
              sx={{
                minWidth: '80px',
                textAlign: 'right',
                fontSize: 'var(--font-size-md)',
                fontWeight: 400,
                color: 'var(--forbrand-primary)',
                whiteSpace: 'nowrap',
              }}
            >
              WHT amount
            </Typography>
          </Box>
          {/* Data rows */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {data.map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  py: 1.5,
                  border: '1px solid var(--border-subtel)',
                  px: 1,
                  mb: 1,
                  gap: 2,
                  backgroundColor: rowColors[index % 3],
                }}
              >
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: 'var(--font-size-md)',
                    fontWeight: 400,
                    color: 'var(--forbrand-primary)',
                  }}
                >
                  {item.varianceType}
                </Typography>
                <Typography
                  sx={{
                    minWidth: '70px',
                    textAlign: 'right',
                    fontSize: 'var(--font-size-md)',
                    fontWeight: 500,
                    color: 'var(--forbrand-primary)',
                    whiteSpace: 'nowrap',
                    px: 1,
                  }}
                >
                  {item.countDisplay}
                </Typography>
                <Typography
                  sx={{
                    minWidth: '80px',
                    textAlign: 'right',
                    fontSize: 'var(--font-size-md)',
                    fontWeight: 500,
                    color: 'var(--forbrand-primary)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.whtAmountDisplay}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default WHTVarianceSummary
